'use client';

import React from 'react';
import { VibeshipProvider as LegacyProvider, VibeshipProviderProps, VibeshipProviderDependencies } from '../components/VibeshipProvider';
import { VibeshipProvider } from './VibeshipProvider';
import { VibeshipDevTools } from './VibeshipDevTools';
import { AISettingsPanel as DefaultAISettingsPanel } from '../components/AISettingsPanel';
import { logger } from '../utils/logger';

// Re-export the dependencies interface for external use
export type { VibeshipProviderDependencies } from '../components/VibeshipProvider';

// Create a context to pass dependencies down
export const DependencyContext = React.createContext<VibeshipProviderDependencies>({});

/**
 * VibeshipProvider with default dependencies that match what the main app expects.
 * This component provides all the same functionality as TaskPanelOverlayWithDeps
 * but using the new provider system.
 */
export function VibeshipProviderWithDeps({
  children,
  config,
  // Allow overriding dependencies
  getProjectTasks,
  sseEndpoint,
  getSSEClient,
  onLoadApiKey,
  chatApiEndpoint,
  AISettingsPanel,
  loadFileContent,
  saveFileContent,
}: VibeshipProviderProps & VibeshipProviderDependencies) {
  logger.log('[VibeshipProviderWithDeps] Rendering with config:', config);
  logger.log('[VibeshipProviderWithDeps] AISettingsPanel provided:', !!AISettingsPanel);
  logger.log('[VibeshipProviderWithDeps] DefaultAISettingsPanel available:', !!DefaultAISettingsPanel);
  logger.log('[VibeshipProviderWithDeps] DefaultAISettingsPanel name:', DefaultAISettingsPanel?.name);
  
  // Store dependencies in a context for VibeshipDevTools to use
  const dependencies = React.useMemo(() => ({
    getProjectTasks,
    sseEndpoint,
    getSSEClient,
    onLoadApiKey,
    chatApiEndpoint,
    AISettingsPanel: AISettingsPanel || DefaultAISettingsPanel,
    loadFileContent,
    saveFileContent,
  }), [
    getProjectTasks,
    sseEndpoint,
    getSSEClient,
    onLoadApiKey,
    chatApiEndpoint,
    AISettingsPanel,
    loadFileContent,
    saveFileContent,
  ]);
  
  logger.log('[VibeshipProviderWithDeps] Dependencies created:', {
    hasAISettingsPanel: !!dependencies.AISettingsPanel,
    AISettingsPanelName: dependencies.AISettingsPanel?.name || 'None'
  });

  // Convert VibecodeConfig to VibeshipConfig format
  const vibeshipConfig = config ? {
    scanPaths: config.scanPaths,
    include: config.include,
    exclude: config.exclude,
    features: config.features,
    ui: {
      theme: config.ui?.theme || 'dark',
      hotkey: config.ui?.hotkey || 'cmd+shift+k',
      position: (config.ui?.position || 'bottom-left') as any,
      showInProduction: config.ui?.showInProduction || false,
      defaultSize: config.ui?.defaultSize 
        ? {
            width: typeof config.ui.defaultSize.width === 'number' ? config.ui.defaultSize.width : 800,
            height: typeof config.ui.defaultSize.height === 'number' ? config.ui.defaultSize.height : 600
          }
        : undefined
    },
    api: config.api 
      ? {
          basePath: config.api.basePath || '/api/vibeship',
          timeout: config.api.timeout || 30000,
          version: config.api.version,
          documentation: config.api.documentation,
          middleware: config.api.middleware
        }
      : undefined,
    cache: config.cache,
    security: config.security,
    taskPatterns: config.taskPatterns
  } as any : undefined;

  logger.log('[VibeshipProviderWithDeps] Rendering provider structure');
  
  return (
    <VibeshipProvider config={vibeshipConfig}>
      <DependencyContext.Provider value={dependencies}>
        {children}
        <VibeshipDevTools />
      </DependencyContext.Provider>
    </VibeshipProvider>
  );
}

// Export hook for accessing dependencies
export const useDependencies = () => {
  return React.useContext(DependencyContext);
};